<template>
  <Teleport to="body">
    <div v-if="item" class="unpack-overlay" @click.self="$emit('cancel')">
      <div class="unpack-modal">

        <!-- Header -->
        <div class="unpack-modal__header">
          <div class="unpack-modal__title">{{ $t('picking.removePackedTitle', { name: item.item?.name }) }}</div>
          <button class="unpack-modal__close" @click="$emit('cancel')" :aria-label="$t('common.close')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Search -->
        <div class="p-3">
          <SearchField
            v-model="search"
            :debounce="0"
            :placeholder="$t('picking.unpackSearch')"
            @update:model-value="page = 1"
          />
        </div>

        <!-- Confirm overlay (replaces list when picking) -->
        <div v-if="pendingCheckout" class="unpack-modal__confirm">
          <div class="unpack-confirm__msg">
            {{ $t('picking.removePackedConfirm', { label: pendingCheckout.label }) }}
          </div>
          <div class="unpack-confirm__sub">{{ $t('picking.packedDate', { date: formatDate(pendingCheckout.checkout_date) }) }}</div>
          <div class="unpack-confirm__actions">
            <button class="unpack-btn unpack-btn--cancel" @click="pendingCheckout = null">{{ $t('common.cancel') }}</button>
            <button class="unpack-btn unpack-btn--confirm" @click="confirmUnpack">{{ $t('picking.yesRemove') }}</button>
          </div>
        </div>

        <!-- Loading -->
        <div v-else-if="loading" class="unpack-modal__state">{{ $t('common.loading') }}</div>

        <!-- Error -->
        <div v-else-if="error" class="unpack-modal__state unpack-modal__state--error">{{ error }}</div>

        <!-- Empty -->
        <div v-else-if="!filtered.length" class="unpack-modal__state">
          {{ checkouts.length ? $t('common.noResults') : $t('picking.noPackedUnits') }}
        </div>

        <!-- List -->
        <div v-else class="unpack-modal__list">
          <button
            v-for="co in pageItems"
            :key="co.id"
            class="unpack-row"
            @click="pendingCheckout = co"
          >
            <div class="unpack-row__label">{{ co.label }}</div>
            <div class="unpack-row__meta">
              <span v-if="co.serial_number && co.asset_tag" class="unpack-row__detail">SN: {{ co.serial_number }}</span>
              <span class="unpack-row__date">{{ formatDate(co.checkout_date) }}</span>
            </div>
          </button>
        </div>

        <!-- Pagination -->
        <div v-if="pageCount > 1 && !pendingCheckout" class="unpack-modal__pagination">
          <button
            class="unpack-page-btn"
            :disabled="page <= 1"
            @click="page--"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <span class="unpack-page-label">{{ page }} / {{ pageCount }}</span>
          <button
            class="unpack-page-btn"
            :disabled="page >= pageCount"
            @click="page++"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { getPackedCheckoutsForSlot } from '@/api/api.js'
import SearchField from '@/components/search/SearchField.vue'

const { t } = useI18n()

const props = defineProps({
  item:      { type: Object, default: null },
  projectId: { type: [String, Number], required: true },
})

const emit = defineEmits(['confirm', 'cancel'])

const PAGE_SIZE = 8

const loading        = ref(false)
const error          = ref(null)
const checkouts      = ref([])
const search         = ref('')
const page           = ref(1)
const pendingCheckout = ref(null)

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return checkouts.value
  return checkouts.value.filter(co =>
    co.label.toLowerCase().includes(q) ||
    (co.serial_number ?? '').toLowerCase().includes(q) ||
    (co.asset_tag     ?? '').toLowerCase().includes(q)
  )
})

const pageCount = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)))

const pageItems = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filtered.value.slice(start, start + PAGE_SIZE)
})

watch(() => props.item, async (newItem) => {
  if (!newItem) return
  // Reset state
  search.value         = ''
  page.value           = 1
  pendingCheckout.value = null
  checkouts.value      = []
  error.value          = null
  loading.value        = true
  try {
    checkouts.value = await getPackedCheckoutsForSlot(props.projectId, newItem)
  } catch (e) {
    error.value = e?.message ?? t('picking.failedLoadPacked')
  } finally {
    loading.value = false
  }
})

function confirmUnpack() {
  if (!pendingCheckout.value) return
  emit('confirm', { checkoutId: pendingCheckout.value.id, item: props.item })
  pendingCheckout.value = null
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  })
}
</script>

<style scoped>
.unpack-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .65);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9999;
  padding: 0 0 env(safe-area-inset-bottom, 0);
}

@media (min-height: 600px) {
  .unpack-overlay {
    align-items: center;
  }
}

.unpack-modal {
  background: #111827;
  border: 1px solid #374151;
  border-radius: 1rem 1rem 0 0;
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (min-height: 600px) {
  .unpack-modal {
    border-radius: 1rem;
    max-height: 75vh;
  }
}

/* Header */
.unpack-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem .75rem;
  border-bottom: 1px solid #1f2937;
  flex-shrink: 0;
}

.unpack-modal__title {
  font-size: .95rem;
  font-weight: 600;
  color: #f9fafb;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: .75rem;
}

.unpack-modal__close {
  background: #1f2937;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;
  flex-shrink: 0;
}
.unpack-modal__close svg { width: 14px; height: 14px; }
.unpack-modal__close:hover { background: #374151; color: #f9fafb; }

/* Search */
.unpack-modal__search {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .75rem 1rem;
  border-bottom: 1px solid #1f2937;
  flex-shrink: 0;
}

.unpack-search__icon { width: 16px; height: 16px; color: #6b7280; flex-shrink: 0; }

.unpack-search__input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #f9fafb;
  font-size: .9rem;
}
.unpack-search__input::placeholder { color: #4b5563; }

.unpack-search__clear {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #6b7280;
  display: flex;
}
.unpack-search__clear svg { width: 15px; height: 15px; }
.unpack-search__clear:hover { color: #9ca3af; }

/* State messages */
.unpack-modal__state {
  padding: 2rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: .9rem;
}
.unpack-modal__state--error { color: #f87171; }

/* List */
.unpack-modal__list {
  flex: 1;
  overflow-y: auto;
  padding: .25rem 0;
}

.unpack-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: .75rem 1rem;
  background: none;
  border: none;
  border-bottom: 1px solid #1f2937;
  cursor: pointer;
  text-align: left;
  transition: background .12s;
  color: inherit;
}
.unpack-row:last-child { border-bottom: none; }
.unpack-row:hover { background: #1f2937; }
.unpack-row:active { background: #374151; }

.unpack-row__label {
  font-size: .95rem;
  font-weight: 600;
  color: #f9fafb;
}

.unpack-row__meta {
  display: flex;
  gap: .75rem;
  margin-top: .2rem;
}

.unpack-row__detail {
  font-size: .78rem;
  color: #9ca3af;
}

.unpack-row__date {
  font-size: .78rem;
  color: #6b7280;
}

/* Confirm step */
.unpack-modal__confirm {
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .5rem;
}

.unpack-confirm__msg {
  font-size: .95rem;
  color: #f9fafb;
  text-align: center;
}

.unpack-confirm__sub {
  font-size: .8rem;
  color: #6b7280;
  margin-bottom: .5rem;
}

.unpack-confirm__actions {
  display: flex;
  gap: .75rem;
  margin-top: .25rem;
}

.unpack-btn {
  padding: .5rem 1.25rem;
  border-radius: .5rem;
  border: none;
  font-size: .9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .12s;
}

.unpack-btn--cancel {
  background: #1f2937;
  color: #d1d5db;
}
.unpack-btn--cancel:hover { background: #374151; }

.unpack-btn--confirm {
  background: #7f1d1d;
  color: #fca5a5;
}
.unpack-btn--confirm:hover { background: #991b1b; }

/* Pagination */
.unpack-modal__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .75rem;
  padding: .75rem 1rem;
  border-top: 1px solid #1f2937;
  flex-shrink: 0;
}

.unpack-page-btn {
  background: #1f2937;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #d1d5db;
  transition: background .12s;
}
.unpack-page-btn:disabled { opacity: .3; cursor: default; }
.unpack-page-btn:not(:disabled):hover { background: #374151; }
.unpack-page-btn svg { width: 16px; height: 16px; }

.unpack-page-label {
  font-size: .85rem;
  color: #9ca3af;
  min-width: 4rem;
  text-align: center;
}
</style>
